import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Eye, Heart, ArrowRight } from "lucide-react";
import { api, COMPANY } from "../lib/api";

const VALUES = [
  { icon: Target, title: "Precision", body: "We obsess over the details — clean architecture, tested deployments, zero surprises." },
  { icon: Eye, title: "Transparency", body: "Clear scopes, honest timelines and pricing you can see. No black boxes." },
  { icon: Heart, title: "Partnership", body: "We invest in your outcomes like they're our own, long after go-live." },
];

export default function WhoWeAre() {
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="border-b border-zinc-200">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-7">
            <span className="font-mono-label text-[11px] text-zinc-400">Who we are</span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mt-3 font-heading text-4xl font-black leading-[0.95] tracking-tighter text-zinc-900 sm:text-5xl lg:text-6xl"
            >
              {content.about_heading || `We are ${COMPANY}`}
            </motion.h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-600">
              {content.about_body ||
                "We help organizations design, build, secure and support the systems that power their work."}
            </p>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://images.pexels.com/photos/12899151/pexels-photo-12899151.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Our team"
              className="h-full w-full border border-zinc-200 object-cover"
            />
          </div>
        </div>
      </section>

      {content.stats?.length > 0 && (
        <section className="border-b border-zinc-200 bg-zinc-50">
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-zinc-200 px-5 sm:px-8 lg:grid-cols-4">
            {content.stats.map((st, i) => (
              <div key={i} className="px-4 py-10 text-center">
                <div className="font-heading text-4xl font-black tracking-tighter text-klein">{st.value}</div>
                <div className="mt-2 font-mono-label text-[11px] text-zinc-500">{st.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <span className="font-mono-label text-[11px] text-zinc-400">What drives us</span>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">Our values</h2>
        <div className="mt-10 grid grid-cols-1 border-l border-t border-zinc-200 md:grid-cols-3">
          {VALUES.map((v) => (
            <div key={v.title} className="border-b border-r border-zinc-200 p-8">
              <div className="flex h-12 w-12 items-center justify-center bg-klein/5 text-klein">
                <v.icon className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <h3 className="mt-5 font-heading text-xl font-semibold text-zinc-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-klein">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-5 py-16 sm:px-8 lg:flex-row lg:items-center">
          <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Let's build something great together.
          </h2>
          <Link to="/lets-build" className="on-accent group flex shrink-0 items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-klein transition-transform hover:-translate-y-0.5">
            Start a project <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
